import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AccountStatementContext } from '../context/AccountStatementContext'
import { useForm, Controller } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { clear, getImageListThunk } from '../../../features/imageListSlice'
import { uploadFile } from '../../../api/image'
import { Button, Switch } from 'ui'
import { Input, Image, Photo } from 'ui/fields'
import TextInputWithButton from './TextInputLabel'
import { useIntl } from 'react-intl'

type Inputs = {
   showBanner: boolean
   showLinkURL: boolean
   image?: Image
   url: string | null
   nonPriorityMembersMessage: string | null
   priorityMembersMessage: string | null
   showPriorityMembersMessage: boolean
   showNonPriorityMembersMessage: boolean
   id: number | null
}
function EditorAccountStatementBanner() {
   const { setImage, config, loyaltyPlanId: planId, onSubmit, isLoading } = useContext(AccountStatementContext)
   const [imageFieldKey, setImageFieldKey] = useState(0)

   const dispatch = useAppDispatch()
   const imageListWrapper = useAppSelector((state) => state.imageListWrapper)
   const basePath = process.env.REACT_APP_BASE_PATH
   const bucket = process.env.REACT_APP_BUCKET
   const intl = useIntl()
   const defaultFormValues = useMemo(
      () => ({
         showBanner: false,
         showLinkURL: false,
         image: undefined,
         url: null,
         nonPriorityMembersMessage: null,
         priorityMembersMessage: null,
      }),
      [],
   )
   const {
      register,
      handleSubmit,
      formState: { errors, dirtyFields },
      control,
      watch,
      setValue,
      reset,
   } = useForm<Inputs>({
      defaultValues: config?.id
         ? {
              id: config.id,
              showBanner: config.isActive ?? false,
              showLinkURL: !!config.link,
              image: config.image,
              url: config.link ?? null,
              nonPriorityMembersMessage: null,
              priorityMembersMessage: null,
           }
         : defaultFormValues,
   })
   const updateFormOnConfigChange = useCallback(
      (currentConfig: typeof config) => {
         if (currentConfig?.id) {
            reset({
               id: currentConfig.id,
               showBanner: currentConfig.isActive,
               showLinkURL: !!currentConfig.link,
               image: currentConfig.image,
               url: currentConfig.link ?? null,
               nonPriorityMembersMessage: null,
               priorityMembersMessage: null,
            })
         } else {
            reset(defaultFormValues)
         }
      },
      [reset, defaultFormValues],
   )

   useEffect(() => {
      updateFormOnConfigChange(config)
   }, [config, updateFormOnConfigChange])

   const handleSetImagePreview = useCallback(
      (event: Image) => {
         setImage(event)
      },
      [setImage],
   )

   const handleGetImageList = useCallback(
      ({ size, sort }: { size: number; sort: Array<string> }) => {
         dispatch(getImageListThunk({ size, sort }))
      },
      [dispatch],
   )
   const showBanner = watch('showBanner')
   const showLinkURL = watch('showLinkURL')
   const idBanner = watch('id')

   const handleShowBannerChange = useCallback(
      (checked: boolean) => {
         if (checked) {
            setValue('showLinkURL', false)
            setValue('url', null)
         } else {
            setValue('image', undefined, { shouldValidate: true })
            setImage(null)
            setImageFieldKey((prev) => prev + 1)
            setValue('showLinkURL', false)
            setValue('url', null)
         }
      },
      [setValue, setImage],
   )

   const handleShowLinkURLChange = useCallback(
      (checked: boolean) => {
         if (!checked) {
            setValue('url', null)
         }
      },
      [setValue],
   )
   return (
      <div className=" mx-auto bg-white shadow-md p-6 rounded-md border w-1/4 h-full">
         <h2 className="text-lg font-semibold mb-4">BANNER</h2>
         {planId && (
            <div className="my-4 text-gray-600 ">
               <p className="mb-2">{intl.formatMessage({ id: 'settings.form.user.tittle' })}</p>
               <TextInputWithButton />
            </div>
         )}
         <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
               name={`showBanner`}
               control={control}
               {...(typeof idBanner === 'number'
                  ? {}
                  : {
                       rules: {
                          required: intl.formatMessage({ id: 'settings.form.showbanner.required' }),
                       },
                    })}
               render={({ field: { value, onChange } }) => {
                  const handleChange = (checked: boolean) => {
                     onChange(checked)
                     handleShowBannerChange(checked)
                  }
                  return (
                     <Switch
                        checked={value}
                        onChange={handleChange}
                        className="my-4"
                        id="showBanner"
                        disabled={!planId}
                     >
                        {intl.formatMessage({ id: 'settings.form.showbanner.tittle' })}
                     </Switch>
                  )
               }}
            />
            {showBanner && (
               <div className=" my-4 rounded-md " key={imageFieldKey}>
                  <Controller
                     name={'image'}
                     control={control}
                     {...(typeof idBanner === 'number'
                        ? {}
                        : {
                             rules: {
                                required: intl.formatMessage({ id: 'settings.form.image.required' }),
                             },
                          })}
                     key={imageFieldKey}
                     render={({ field: { onChange, value, ...rest } }) => {
                        const onChangeFile = (event: any) => {
                           handleSetImagePreview(event)
                           onChange(event)
                        }
                        return (
                           <>
                              <Photo
                                 key={imageFieldKey}
                                 value={watch('image')}
                                 {...rest}
                                 basePath={`${basePath}${bucket}/`}
                                 clearImageList={clear}
                                 imageListWrapper={{
                                    isFetching: imageListWrapper.isFetching,
                                    items: imageListWrapper.items,
                                 }}
                                 getImageList={handleGetImageList}
                                 single={true}
                                 aspectRatio={'WIDE'}
                                 uploadFile={uploadFile}
                                 onChange={onChangeFile}
                              />
                              {errors?.image?.message && (
                                 <span className="text-sm text-red-700">{errors.image.message}</span>
                              )}
                           </>
                        )
                     }}
                  />
                  <p className="text-xs mt-2 text-gray-500">JPG o PNG de 455px</p>
               </div>
            )}

            <Controller
               name={`showLinkURL`}
               control={control}
               render={({ field: { value, onChange } }) => {
                  const handleChange = (checked: boolean) => {
                     onChange(checked)
                     handleShowLinkURLChange(checked)
                  }
                  return (
                     <Switch
                        checked={value}
                        onChange={handleChange}
                        className="my-4"
                        id="showLinkURL"
                        disabled={!planId}
                     >
                        {intl.formatMessage({ id: 'settings.form.showlink.tittle' })}
                     </Switch>
                  )
               }}
            />
            {showLinkURL && (
               <Input
                  boldLabel={false}
                  type="text"
                  label={intl.formatMessage({ id: 'settings.form.link.tittle' })}
                  {...register('url', {
                     required: 'Campo requerido',
                     pattern: {
                        value: /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,6}(:\d{1,5})?(\/\S*)?$/i,
                        message: intl.formatMessage({ id: 'settings.form.link.validate' }),
                     },
                  })}
                  error={errors?.url?.message}
                  isTouched={dirtyFields.url}
                  containerClassName="my-4  text-gray-700 pr-4"
               />
            )}
            <Button
               className=" px-4 py-2 rounded-md mt-4 w-full"
               buttonStyle={{ background: '#f59e0b', color: 'white' }}
               type="submit"
               isLoading={isLoading}
            >
               <span className="text-sm font-semibold">{intl.formatMessage({ id: 'settings.form.save.tittle' })}</span>
            </Button>
            {errors?.showBanner?.message && (
               <span className="text-sm text-red-700 text-center">{errors.showBanner.message}</span>
            )}
         </form>
      </div>
   )
}

export default EditorAccountStatementBanner
